import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  Sun,
  Moon,
  Monitor as MonitorIcon,
  ChevronDown,
  Bell,
  Search,
  LogOut,
  UserCircle,
  Settings,
  Check,
  ArrowRight,
  CornerDownLeft,
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  Wallet,
  Navigation,
  Megaphone,
  Monitor as MonitorScreen,
  Tv,
  Image as ImageIcon,
  Building2,
  Shield,
  ShieldCheck,
  CalendarDays,
  Plug,
  BarChart3,
  TrendingUp,
  FileText,
  Power,
  Trash2,
  Briefcase,
  ReceiptText,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../theme/ThemeContext";
import { useModules } from "../theme/ModuleContext";
import API from "../services/api";
import WebAttendance from "../components/WebAttendance";

/**
 * Global route catalog used by the header search. Each entry is gated by
 * the same permission keys the sidebar uses, so the search can never
 * recommend a URL the current admin cannot open.
 *
 *   permission     — single key required (string)
 *   anyPermission  — array of keys; user must have at least one
 *   (none)         — open to every authenticated admin
 *
 * `keywords` powers fuzzy matching beyond the visible label so admins can
 * type natural terms like "petrol", "geofence", or "rbac".
 */
const ROUTES = [
  { to: "/admin",                            label: "Dashboard",            group: "Main",          Icon: LayoutDashboard, keywords: "home overview stats" },
  { to: "/admin/employees",                  label: "Employees",            group: "People",        Icon: Users,           keywords: "users staff team people directory",   permission: "users.view", moduleKey: "employees" },
  { to: "/admin/attendance",                 label: "Attendance",           group: "People",        Icon: Clock,           keywords: "punch checkin checkout clock log timesheet", moduleKey: "attendance" },
  { to: "/admin/my-monitoring-hours",        label: "My Monitoring Hours",  group: "People",        Icon: Clock,           keywords: "own productivity idle tracker hours",                                       permission: "monitor.self.view", moduleKey: "tracker" },
  { to: "/admin/leaves",                     label: "Leaves",               group: "People",        Icon: Calendar,        keywords: "leave request time off vacation approval",                                  moduleKey: "leaves" },
  { to: "/admin/leave-balances",             label: "Leave Balances",       group: "People",        Icon: Wallet,          keywords: "balance allocation pl sl cl carry forward",                                permission: "leaves.balance.write", moduleKey: "leaves" },
  { to: "/admin/payroll",                    label: "Payroll",              group: "People",        Icon: Wallet,          keywords: "salary payslip wages compensation pay hr",                                anyPermission: ["payroll.view", "payroll.write"], moduleKey: "payroll" },
  { to: "/admin/travel-logs",                label: "Travel Logs",          group: "Field",         Icon: Navigation,      keywords: "trips km petrol field visit reimbursement client mileage",               anyPermission: ["travel.view", "travel.approve"], moduleKey: "travel" },
  { to: "/admin/reimbursements",             label: "Expense Claims",       group: "Field",         Icon: ReceiptText,     keywords: "expense claims reimbursement receipts invoices bills finance paid",      anyPermission: ["reimbursements.view_own", "reimbursements.view", "reimbursements.view_all", "reimbursements.create_own", "reimbursements.create", "reimbursements.approve", "reimbursements.finance"], moduleKey: "reimbursements" },
  { to: "/admin/announcements",              label: "Announcements",        group: "Communication", Icon: Megaphone,       keywords: "news broadcast notice memo",                                              permission: "announcements.view", moduleKey: "announcements" },
  { to: "/admin/documents",                  label: "Documents",            group: "Operations",    Icon: FileText,       keywords: "documents files hr asset invoice warranty policies certificates",          permission: "documents.view", moduleKey: "documents" },
  { to: "/admin/projects",                   label: "Projects & Tasks",     group: "Operations",    Icon: Briefcase,      keywords: "projects tasks kanban assignment delivery due dates work",                anyPermission: ["projects.view", "projects.view_all", "projects.view_own"], moduleKey: "projects" },
  { to: "/admin/reports",                    label: "Advanced Reports",     group: "Analytics",     Icon: BarChart3,       keywords: "advanced reports analytics excel pdf csv scheduled templates builder",      permission: "reports.view", moduleKey: "reports" },
  { to: "/admin/system",                     label: "System Admin",         group: "System",        Icon: ShieldCheck,     keywords: "system audit sessions api tokens jobs health failed logins",               anyPermission: ["system.view", "system.audit", "system.sessions", "system.tokens", "system.jobs", "system.export"], moduleKey: "system" },

  /* Monitoring */
  { to: "/admin/monitoring/dashboard",       label: "Monitor Dashboard",    group: "Monitoring",    Icon: MonitorScreen,   keywords: "monitor overview activity productivity",                                  anyPermission: ["monitor.view", "monitor.live", "monitor.delete"], moduleKey: "tracker" },
  { to: "/admin/monitoring/users",           label: "Monitor Users",        group: "Monitoring",    Icon: Users,           keywords: "monitoring people sessions",                                              anyPermission: ["monitor.view", "monitor.live", "monitor.delete"], moduleKey: "tracker" },
  { to: "/admin/monitoring/live-screens",    label: "Live Screens",         group: "Monitoring",    Icon: Tv,              keywords: "live stream realtime watching",                                           anyPermission: ["monitor.view", "monitor.live"], moduleKey: "tracker" },
  { to: "/admin/monitoring/screenshots",     label: "Screenshots",          group: "Monitoring",    Icon: ImageIcon,       keywords: "screen captures images history",                                          anyPermission: ["monitor.view", "monitor.delete"], moduleKey: "tracker" },
  { to: "/admin/monitoring/productivity",    label: "Productivity",         group: "Monitoring",    Icon: TrendingUp,      keywords: "productivity productive idle time sessions employee tracking",             anyPermission: ["monitor.view"], moduleKey: "tracker" },
  { to: "/admin/monitoring/shutdown-logs",   label: "Shutdown Logs",        group: "Monitoring",    Icon: Power,           keywords: "shutdown laptop power off end day alert",                                  anyPermission: ["monitor.view"], moduleKey: "tracker" },
  { to: "/admin/monitoring/reports",         label: "Monitor Reports",      group: "Monitoring",    Icon: BarChart3,       keywords: "reports analytics productivity export",                                    permission: "reports.view", moduleKey: "tracker" },

  /* Settings */
  { to: "/admin/settings/attendance",        label: "Attendance Settings",  group: "Settings",      Icon: Clock,           keywords: "work hours geofence office shift petrol allowance days late",             anyPermission: ["settings.view", "settings.write"], moduleKey: "attendance" },
  { to: "/admin/settings/leave-policy",      label: "Leave Policy",         group: "Settings",      Icon: Calendar,        keywords: "leave rules approval auto reportee total balance",                       anyPermission: ["settings.view", "settings.leave.write"], moduleKey: "leaves" },
  { to: "/admin/settings/monitoring",        label: "Monitoring Settings",  group: "Settings",      Icon: MonitorScreen,   keywords: "screenshot interval idle threshold capture",                              anyPermission: ["settings.view", "settings.monitor.write"], moduleKey: "tracker" },
  { to: "/admin/settings/notifications",     label: "Notification Settings",group: "Settings",      Icon: Bell,            keywords: "alerts smtp email push notify",                                           anyPermission: ["settings.view", "settings.write"] },
  { to: "/admin/settings/company",           label: "Company & Branding",   group: "Settings",      Icon: Building2,       keywords: "brand logo color name organization theme",                                anyPermission: ["settings.view", "settings.write"] },
  { to: "/admin/settings/security",          label: "Security",             group: "Settings",      Icon: Shield,          keywords: "password 2fa ip session lockout",                                         anyPermission: ["settings.view", "settings.write"] },
  { to: "/admin/settings/maintenance",       label: "Maintenance Mode",     group: "Settings",      Icon: Shield,          keywords: "maintenance downtime offline 503 service unavailable",                   anyPermission: ["settings.view", "settings.write"] },
  { to: "/admin/settings/holidays",          label: "Holidays",             group: "Settings",      Icon: CalendarDays,    keywords: "public holiday list calendar",                                            permission: "holidays.write", moduleKey: "leaves" },
  { to: "/admin/settings/integrations",      label: "Integrations",         group: "Settings",      Icon: Plug,            keywords: "api webhook slack google calendar zapier",                                anyPermission: ["settings.view", "settings.write"] },
  { to: "/admin/settings/permissions",       label: "Permissions",          group: "Settings",      Icon: ShieldCheck,     keywords: "rbac roles access control",                                               permission: "permissions.write" },
];

/**
 * Header — top application bar used on every admin page.
 *
 *   • Mobile: shows a hamburger that opens the sidebar drawer.
 *   • Desktop: pure branding + actions (sidebar owns left rail).
 *
 *   Controls: global search (placeholder), theme picker, notifications,
 *             and a user profile dropdown.
 *
 * Dark mode is handled via Tailwind `dark:` classes — this file is
 * fully dark-mode aware.
 */
export default function Header({ isMobile, onOpenMobileSidebar, sidebarCollapsed }) {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();

  const left = isMobile ? 0 : sidebarCollapsed ? 80 : 256;

  return (
    <header
      style={{ left }}
      className={[
        "fixed top-0 right-0 z-20 h-16",
        "flex items-center gap-2 md:gap-3 px-3 md:px-6",
        "bg-white/80 dark:bg-slate-900/80 backdrop-blur",
        "border-b border-gray-100 dark:border-slate-800",
        "transition-[left] duration-300 ease-in-out",
      ].join(" ")}
    >
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          type="button"
          onClick={onOpenMobileSidebar}
          aria-label="Open menu"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition"
        >
          <Menu size={22} className="text-gray-700 dark:text-gray-200" />
        </button>
      )}

      {/* Search (hidden on tiny screens) */}
      <GlobalSearch />

      {/* Right cluster */}
      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <WebAttendance />
        <ThemePicker />
        <NotificationsBell />
        <ProfileMenu
          user={user}
          onLogout={logout}
          onProfile={() => navigate("/admin/my-profile")}
          onAccount={() => navigate("/admin/settings/security")}
        />
      </div>
    </header>
  );
}

/* ───────────────────────── GLOBAL SEARCH ───────────────────────── */

/**
 * GlobalSearch — keyboard-first command palette built into the header.
 *
 * Behaviour:
 *   • Click / focus / `/` shortcut opens the dropdown.
 *   • Empty query  → "Recommended" — top accessible routes (capped to 6).
 *   • Typed query  → fuzzy-ranked matches over `label` + `keywords`.
 *   • Arrow ↑/↓    → move highlight.
 *   • Enter        → navigate.
 *   • Escape       → close (input stays focused).
 *   • Click outside → close.
 *
 * RBAC: routes are filtered up-front via `hasPermission`, so the dropdown
 * never recommends a URL the current admin would land on a Forbidden page
 * for. Mirrors the same gating logic the Sidebar uses.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth() || {};
  const { isModuleEnabled } = useModules();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useClickOutside(wrapRef, () => setOpen(false));

  // Allowed routes for this admin (computed once per render via hasPermission)
  const allowed = useMemo(() => {
    const can = (r) => {
      if (r.moduleKey && !isModuleEnabled(r.moduleKey)) return false;
      if (!hasPermission) return true; // permissive while auth context boots
      if (r.permission)    return hasPermission(r.permission);
      if (r.anyPermission) return r.anyPermission.some((k) => hasPermission(k));
      return true;
    };
    return ROUTES.filter(can);
  }, [hasPermission, isModuleEnabled]);

  // Score each route against the query; higher = better match.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query → "recommended" tray (top 6 accessible)
      return allowed.slice(0, 6).map((r) => ({ ...r, _recommended: true }));
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    const scored = [];
    for (const r of allowed) {
      const label = r.label.toLowerCase();
      const haystack = `${label} ${r.keywords || ""} ${r.to}`.toLowerCase();
      let score = 0;
      let matchedAll = true;
      for (const t of tokens) {
        if (label.startsWith(t))      score += 100;
        else if (label.includes(t))   score += 60;
        else if (haystack.includes(t)) score += 25;
        else { matchedAll = false; break; }
      }
      if (matchedAll) scored.push({ r, score });
    }
    scored.sort((a, b) => b.score - a.score || a.r.label.localeCompare(b.r.label));
    return scored.slice(0, 8).map(({ r }) => r);
  }, [allowed, query]);

  // Keep highlight in range whenever the result list changes
  useEffect(() => { setActive(0); }, [query, open]);

  // Global "/" shortcut to focus the search bar
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target.tagName || "").toLowerCase();
      const isField = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      if (isField) return;
      e.preventDefault();
      inputRef.current?.focus();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (r) => {
    if (!r) return;
    setOpen(false);
    setQuery("");
    navigate(r.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Scroll active row into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return (
    <div
      ref={wrapRef}
      className="hidden sm:flex items-center gap-2 flex-1 max-w-md ml-1 relative"
    >
      <label className="relative w-full block">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search pages…  (press /)"
          aria-label="Search admin pages"
          aria-expanded={open}
          aria-controls="global-search-results"
          role="combobox"
          autoComplete="off"
          className={[
            "w-full pl-9 pr-12 py-2 rounded-xl text-sm",
            "bg-gray-100/70 dark:bg-slate-800/70",
            "text-gray-800 dark:text-gray-100",
            "placeholder:text-gray-400 dark:placeholder:text-gray-500",
            "border border-transparent focus:border-blue-400 dark:focus:border-blue-500",
            "focus:bg-white dark:focus:bg-slate-800 focus:outline-none",
            "focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-blue-500/20",
            "transition",
          ].join(" ")}
        />
        {!open && !query && (
          <kbd
            aria-hidden
            className={[
              "hidden md:flex absolute right-3 top-1/2 -translate-y-1/2",
              "items-center justify-center min-w-[20px] h-5 px-1.5 rounded",
              "text-[10px] font-medium",
              "bg-white dark:bg-slate-700",
              "text-gray-500 dark:text-gray-300",
              "border border-gray-200 dark:border-slate-600",
            ].join(" ")}
          >
            /
          </kbd>
        )}
      </label>

      {open && (
        <div
          id="global-search-results"
          role="listbox"
          ref={listRef}
          className={[
            "absolute left-0 right-0 top-full mt-2",
            "rounded-xl shadow-xl border overflow-hidden",
            "bg-white dark:bg-slate-900",
            "border-gray-100 dark:border-slate-800",
            "animate-slide-down z-30",
            "max-h-[70vh] overflow-y-auto",
          ].join(" ")}
        >
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <p>No pages match “{query}”.</p>
              <p className="text-xs mt-1 opacity-70">Try a label, keyword, or URL fragment.</p>
            </div>
          ) : (
            <>
              {!query && (
                <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
                  Recommended
                </div>
              )}
              <ul className="p-1">
                {results.map((r, i) => (
                  <SearchResultRow
                    key={r.to}
                    idx={i}
                    route={r}
                    active={i === active}
                    query={query}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                  />
                ))}
              </ul>
              <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 dark:border-slate-800 text-[10px] text-gray-400 dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">↑↓</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 inline-flex items-center">
                    <CornerDownLeft size={10} />
                  </kbd>
                  open
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">esc</kbd>
                  close
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SearchResultRow({ idx, route, active, query, onMouseEnter, onClick }) {
  const Icon = route.Icon || Search;
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={active}
        data-idx={idx}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        className={[
          "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left",
          "transition",
          active
            ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-200"
            : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800/70",
        ].join(" ")}
      >
        <span
          className={[
            "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
            active
              ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-200"
              : "bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400",
          ].join(" ")}
        >
          <Icon size={14} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium truncate">
            {highlightMatch(route.label, query)}
          </span>
          <span className="block text-[11px] text-gray-400 dark:text-gray-500 truncate">
            {route.group} · {route.to}
          </span>
        </span>
        <ArrowRight
          size={14}
          className={active ? "opacity-100" : "opacity-0 transition-opacity"}
        />
      </button>
    </li>
  );
}

/** Wrap matched substrings in <mark> so the dropdown highlights what hit. */
function highlightMatch(label, query) {
  const q = query.trim();
  if (!q) return label;
  const lc = label.toLowerCase();
  const lq = q.toLowerCase();
  const idx = lc.indexOf(lq);
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="bg-transparent text-blue-600 dark:text-blue-300 font-semibold">
        {label.slice(idx, idx + q.length)}
      </mark>
      {label.slice(idx + q.length)}
    </>
  );
}

/* ───────────────────────── THEME PICKER ───────────────────────── */

function ThemePicker() {
  const { mode, resolved, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  useEscape(() => setOpen(false), open);

  const icon =
    mode === "system" ? (
      <MonitorIcon size={18} />
    ) : resolved === "dark" ? (
      <Moon size={18} />
    ) : (
      <Sun size={18} />
    );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Change theme"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={[
          "p-2 rounded-xl",
          "text-gray-700 dark:text-gray-200",
          "hover:bg-gray-100 dark:hover:bg-slate-800",
          "active:scale-95 transition",
        ].join(" ")}
      >
        {icon}
      </button>

      {open && (
        <div
          role="menu"
          className={[
            "absolute right-0 mt-2 w-44 origin-top-right",
            "rounded-xl shadow-xl border",
            "bg-white dark:bg-slate-900",
            "border-gray-100 dark:border-slate-800",
            "animate-slide-down",
            "p-1",
          ].join(" ")}
        >
          <ThemeRow active={mode === "light"}  onClick={() => { setMode("light");  setOpen(false); }} Icon={Sun}          label="Light" />
          <ThemeRow active={mode === "dark"}   onClick={() => { setMode("dark");   setOpen(false); }} Icon={Moon}         label="Dark" />
          <ThemeRow active={mode === "system"} onClick={() => { setMode("system"); setOpen(false); }} Icon={MonitorIcon}  label="System" />
        </div>
      )}
    </div>
  );
}

function ThemeRow({ active, onClick, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="menuitemradio"
      aria-checked={active}
      className={[
        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm",
        "text-gray-700 dark:text-gray-200",
        "hover:bg-gray-100 dark:hover:bg-slate-800",
        "transition",
      ].join(" ")}
    >
      <Icon size={16} />
      <span className="flex-1 text-left">{label}</span>
      {active && <Check size={14} className="text-blue-600 dark:text-blue-400" />}
    </button>
  );
}

/* ───────────────────────── NOTIFICATIONS ───────────────────────── */

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  useEscape(() => setOpen(false), open);

  const unreadCount = useMemo(
    () => items.filter((n) => !n.readStatus).length,
    [items]
  );

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await API.get("/notification/all");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) loadNotifications();
      return next;
    });
  };

  const markRead = async (item) => {
    if (!item || item.readStatus) return;
    setItems((prev) => prev.map((n) => (
      n.id === item.id ? { ...n, readStatus: true } : n
    )));
    try {
      await API.post(`/notification/read/${item.id}`);
    } catch (_) {
      loadNotifications();
    }
  };

  const removeItem = async (item) => {
    if (!item) return;
    const snapshot = items;
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    try {
      await API.delete(`/notification/${item.id}`);
    } catch (_) {
      setItems(snapshot);
    }
  };

  const clearAll = async () => {
    if (!items.length) return;
    const snapshot = items;
    setItems([]);
    try {
      await API.delete("/notification/all");
    } catch (_) {
      setItems(snapshot);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={[
          "relative p-2 rounded-xl",
          "text-gray-700 dark:text-gray-200",
          "hover:bg-gray-100 dark:hover:bg-slate-800",
          "active:scale-95 transition",
        ].join(" ")}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className={[
              "absolute top-1 right-1",
              "min-w-[18px] h-[18px] px-1 rounded-full",
              "text-[10px] font-bold text-white",
              "bg-red-500 flex items-center justify-center",
              "ring-2 ring-white dark:ring-slate-900",
            ].join(" ")}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className={[
            "absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-24px)]",
            "rounded-xl shadow-xl border overflow-hidden z-50",
            "bg-white dark:bg-slate-900",
            "border-gray-100 dark:border-slate-800",
            "animate-slide-down",
          ].join(" ")}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-800">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {unreadCount ? `${unreadCount} unread` : `${items.length} total`}
              </div>
            </div>
            {items.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Loading notifications...</div>
            ) : error ? (
              <div className="px-4 py-8 text-center">
                <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
                <button type="button" onClick={loadNotifications} className="mt-3 text-xs font-semibold text-blue-600 dark:text-blue-400">
                  Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">No notifications yet.</div>
            ) : (
              items.slice(0, 20).map((item) => (
                <div
                  key={item.id}
                  className={[
                    "group flex gap-3 px-4 py-3 border-b last:border-b-0",
                    "border-gray-100 dark:border-slate-800",
                    item.readStatus ? "bg-white dark:bg-slate-900" : "bg-blue-50/70 dark:bg-blue-950/20",
                  ].join(" ")}
                >
                  <button
                    type="button"
                    onClick={() => markRead(item)}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {item.title || "Notification"}
                        </div>
                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                          {item.message || ""}
                        </div>
                        <div className="mt-2 text-[11px] text-gray-400">
                          {formatNotificationTime(item.createdAt)}
                        </div>
                      </div>
                      {!item.readStatus && <span className="mt-1 h-2 w-2 rounded-full bg-blue-600" />}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(item)}
                    title="Delete notification"
                    className="h-8 w-8 shrink-0 rounded-lg inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── PROFILE MENU ───────────────────────── */

function ProfileMenu({ user, onLogout, onProfile, onAccount }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  useEscape(() => setOpen(false), open);

  const display = useMemo(() => deriveDisplay(user), [user]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={[
          "flex items-center gap-2 p-1 pr-2 sm:pr-3 rounded-xl",
          "hover:bg-gray-100 dark:hover:bg-slate-800 transition",
        ].join(" ")}
      >
        <Avatar display={display} />
        <span className="hidden sm:flex flex-col text-left leading-tight">
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate max-w-[140px]">
            {display.name}
          </span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[140px]">
            {display.subtitle}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`hidden sm:block text-gray-400 dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className={[
            "absolute right-0 mt-2 w-64 origin-top-right",
            "rounded-xl shadow-xl border",
            "bg-white dark:bg-slate-900",
            "border-gray-100 dark:border-slate-800",
            "animate-slide-down",
          ].join(" ")}
        >
          {/* Profile card */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-slate-800">
            <Avatar display={display} size="lg" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                {display.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {display.email || display.subtitle}
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {display.roles.slice(0, 3).map((r) => (
                  <span
                    key={r}
                    className={[
                      "text-[10px] font-medium px-1.5 py-0.5 rounded-full",
                      "bg-blue-50 text-blue-700",
                      "dark:bg-blue-500/10 dark:text-blue-300",
                    ].join(" ")}
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="p-1">
            <MenuRow Icon={UserCircle} label="My profile"       onClick={onProfile} />
            <MenuRow Icon={Settings}   label="Account settings" onClick={onAccount} />
            <div className="my-1 h-px bg-gray-100 dark:bg-slate-800" />
            <MenuRow
              Icon={LogOut}
              label="Logout"
              onClick={onLogout}
              danger
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuRow({ Icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="menuitem"
      className={[
        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm",
        "transition",
        danger
          ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
          : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800",
      ].join(" ")}
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
}

function Avatar({ display, size = "md" }) {
  const dim = size === "lg" ? "w-10 h-10 text-sm" : "w-8 h-8 text-xs";
  return display.image ? (
    <img
      src={display.image}
      alt={display.name}
      className={`${dim} rounded-full object-cover ring-2 ring-white dark:ring-slate-900 shadow`}
    />
  ) : (
    <div
      aria-hidden
      className={[
        dim,
        "rounded-full font-semibold text-white flex items-center justify-center shadow",
        "bg-gradient-to-br",
        display.color,
      ].join(" ")}
    >
      {display.initials}
    </div>
  );
}

/* ───────────────────────── helpers ───────────────────────── */

function deriveDisplay(user) {
  const roles = extractRoles(user);
  const name = user?.name || user?.username || user?.sub || "Guest";
  const email = user?.email || "";
  const initials = computeInitials(name);
  const subtitle = roles.length ? roles.join(" · ") : email || "Not signed in";
  const image = user?.profilePic || user?.avatar || null;
  const color = gradientForName(name);
  return { name, email, roles, initials, subtitle, image, color };
}

function extractRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles)) return user.roles.map(normalize);
  if (Array.isArray(user.authorities)) {
    return user.authorities
      .map((a) => (typeof a === "string" ? a : a?.authority))
      .filter(Boolean)
      .map(normalize);
  }
  if (typeof user.role === "string") return [normalize(user.role)];
  return [];
}
function normalize(role) {
  return String(role).replace(/^ROLE_/, "").toUpperCase();
}

function computeInitials(name) {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("") || "?";
}

const GRADIENTS = [
  "from-blue-500 to-indigo-600",
  "from-purple-500 to-pink-500",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
  "from-rose-500 to-red-600",
  "from-cyan-500 to-sky-600",
];
function gradientForName(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}

function formatNotificationTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function useClickOutside(ref, cb) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) cb();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, cb]);
}

function useEscape(cb, active) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") cb(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cb, active]);
}
